import { showToast } from "../../components/toastService.jsx";
import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const RecentlyDeletedPanel = withListOverflow(RecentlyDeletedListOwner);
// src/features/admin/RecentlyDeletedPanel.jsx
//
// G-21 SOFT-DELETE-TRASH-RESTORE: Admin → Recently Deleted panel.
//
// Lists soft-deleted items (In Focus items, candidates, review items) that
// are within the 30-day retention window. Admin/editor users can restore
// any item to its original location.
//
// Layout: panel header + type filter chips + search input + items table.
// Restore writes audit_log via POST /api/admin/recently-deleted/:type/:id/restore.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Crosshair, Building2, ClipboardList, FileText } from "lucide-react";
import { T } from "../../app/tokens.js";
import { getRecentlyDeleted, restoreDeletedItem } from "../../data/adminClient.js";
import { log } from "../../utils/logger.js";

// ─── Type chip colours ────────────────────────────────────────────────────────

const TYPE_STYLES = {
  in_focus:  { bg: T.purpleBg,   color: T.decisionFg,  label: "In Focus",  icon: Crosshair },
  candidate: { bg: T.blueSoft,   color: T.blue,         label: "Candidate", icon: Building2 },
  review:    { bg: T.ragAmberBg, color: T.ragAmberText, label: "Review",    icon: ClipboardList },
  lease:     { bg: T.purpleBg,   color: T.decisionFg,   label: "Lease",     icon: FileText },
};

function TypeChip({ type }) {
  const s = TYPE_STYLES[type] ?? { bg: T.lightGray, color: T.mid, label: type };
  return (
    <span
      style={{
        fontSize:     T.fsXxs,
        fontWeight:   T.fwExtrabold,
        padding:      "2px 8px",
        borderRadius: 6,
        background:   s.bg,
        color:        s.color,
        whiteSpace:   "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Relative time formatter ──────────────────────────────────────────────────

function relativeTime(isoStr) {
  if (!isoStr) return "—";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins < 60)  return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  return new Date(isoStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function retentionLabel(days) {
  if (days <= 0) return { text: "Expiring soon", soon: true };
  return { text: `${days} day${days !== 1 ? "s" : ""} left`, soon: days <= 7 };
}

// ─── Filter chip ─────────────────────────────────────────────────────────────

function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize:     T.fsXs5,
        fontWeight:   T.fwBold,
        padding:      "4px 12px",
        borderRadius: 999,
        border:       `1px solid ${active ? T.dark : T.border}`,
        background:   active ? T.dark : T.card,
        color:        active ? T.white : T.mid,
        cursor:       "pointer",
        whiteSpace:   "nowrap",
      }}
    >
      {label}{count != null ? ` · ${count}` : ""}
    </button>
  );
}

// ─── Table row ───────────────────────────────────────────────────────────────

function ItemRow({ item, onRestore, restoring }) {
  const ret = retentionLabel(item.retentionDays);
  const s   = TYPE_STYLES[item.type];
  const Icon = s?.icon ?? null;
  return (
    <tr>
      <td
        style={{
          padding:       "11px 14px 11px 18px",
          borderTop:     `1px solid ${T.border}`,
          verticalAlign: "middle",
          width:         32,
        }}
        data-testid={`type-icon-cell-${item.type}`}
      >
        {Icon && (
          <Icon
            size={16}
            style={{ color: s.color, display: "block" }}
            aria-label={s.label}
          />
        )}
      </td>
      <td
        style={{
          padding:        "11px 18px",
          borderTop:      `1px solid ${T.border}`,
          verticalAlign:  "middle",
        }}
      >
        <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm5 }}>{item.name}</div>
        {typeof item.reason === "string" && item.reason.trim() && (
          <div data-testid="archive-reason" style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwRegular, marginTop: 2, overflowWrap: "anywhere", maxWidth: 480 }}>
            “{item.reason.trim()}”
          </div>
        )}
        {item.subtitle && (
          <div style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwRegular, marginTop: 2 }}>
            {item.subtitle}
          </div>
        )}
      </td>
      <td style={{ padding: "11px 18px", borderTop: `1px solid ${T.border}`, verticalAlign: "middle" }}>
        <TypeChip type={item.type} />
      </td>
      <td
        style={{
          padding:       "11px 18px",
          borderTop:     `1px solid ${T.border}`,
          verticalAlign: "middle",
          color:         T.mid,
          fontSize:      T.fsSm5,
        }}
      >
        {item.deletedBy || "—"}
      </td>
      <td
        style={{
          padding:       "11px 18px",
          borderTop:     `1px solid ${T.border}`,
          verticalAlign: "middle",
          color:         T.mid,
          fontSize:      T.fsSm5,
        }}
      >
        {relativeTime(item.deletedAt)}
      </td>
      <td
        style={{
          padding:       "11px 18px",
          borderTop:     `1px solid ${T.border}`,
          verticalAlign: "middle",
          fontSize:      T.fsSm,
          color:         T.mid,
        }}
      >
        <span style={{ fontWeight: T.fwBold, color: ret.soon ? T.ragAmberText : T.dark }}>
          {ret.text}
        </span>
      </td>
      <td
        style={{
          padding:       "11px 18px",
          borderTop:     `1px solid ${T.border}`,
          verticalAlign: "middle",
          textAlign:     "right",
        }}
      >
        <button
          data-testid={`restore-btn-${item.id}`}
          onClick={() => onRestore(item)}
          disabled={restoring === item.id}
          style={{
            color:       T.blue,
            fontWeight:  T.fwBold,
            fontSize:    T.fsSm5,
            border:      `1px solid ${T.blueSoftBorder}`,
            borderRadius: 8,
            padding:     "5px 12px",
            background:  T.card,
            cursor:      restoring === item.id ? "default" : "pointer",
            whiteSpace:  "nowrap",
            opacity:     restoring === item.id ? 0.5 : 1,
          }}
        >
          {restoring === item.id ? "Restoring…" : "Restore"}
        </button>
      </td>
    </tr>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TYPE_FILTERS = [
  { key: "all",       label: "All" },
  { key: "in_focus",  label: "In Focus" },
  { key: "candidate", label: "Candidates" },
  { key: "review",    label: "Reviews" },
  { key: "lease",     label: "Leases" },
];

function RecentlyDeletedListOwner({ explainer } = {}) {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [search,    setSearch]    = useState("");
  const [restoring, setRestoring] = useState(null); // id of item being restored
  const [toast,     setToast]     = useState(null);
  const toastTimer = useRef(null);

  // ── Load items ──────────────────────────────────────────────────────────────
  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (search.trim())        params.q    = search.trim();
      const data = await getRecentlyDeleted(params);
      setItems(data.items ?? []);
    } catch (err) {
      log.warn("RecentlyDeletedPanel: load failed", { err: String(err) });
      setError("Failed to load deleted items. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, search]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ── Count chips ─────────────────────────────────────────────────────────────
  // We always show unfiltered counts for the chip labels. Load a base set
  // without filters for the counts, but display the filtered set in the table.
  const [allItems, setAllItems] = useState([]);
  useEffect(() => {
    getRecentlyDeleted({}).then((d) => setAllItems(d.items ?? [])).catch(() => {});
  }, [restoring]); // refresh counts when an item is restored

  const countByType = {};
  for (const item of allItems) {
    countByType[item.type] = (countByType[item.type] ?? 0) + 1;
  }

  // ── Restore ─────────────────────────────────────────────────────────────────
  const handleRestore = useCallback(async (item) => {
    setRestoring(item.id);
    try {
      await restoreDeletedItem(item.type, item.id);
      clearTimeout(toastTimer.current);
      setToast(`"${item.name}" restored successfully.`);
      toastTimer.current = setTimeout(() => setToast(null), 4000);
      await loadItems();
    } catch (err) {
      log.warn("RecentlyDeletedPanel: restore failed", { err: String(err), id: item.id });
      showToast(`Failed to restore "${item.name}". It may have already been restored or expired.`, { type: "error" });
    } finally {
      setRestoring(null);
    }
  }, [loadItems]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div data-testid="recently-deleted-panel">
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: T.fsLg, fontWeight: T.fwBold, color: T.dark }}>
          Recently deleted
        </h2>
      </div>
      {explainer}

      {/* Toast notification */}
      {toast && (
        <div
          data-testid="recently-deleted-toast"
          style={{
            marginBottom:  12,
            padding:       "10px 14px",
            background:    T.blueSoft,
            border:        `1px solid ${T.blueSoftBorder}`,
            borderRadius:  8,
            fontSize:      T.fsSm,
            color:         T.blue,
            fontWeight:    T.fwSemibold,
          }}
        >
          {toast}
        </div>
      )}

      {/* Filter chips + search */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        {TYPE_FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            label={f.label}
            count={f.key === "all" ? allItems.length : (countByType[f.key] ?? 0)}
            active={typeFilter === f.key}
            onClick={() => setTypeFilter(f.key)}
          />
        ))}
        <div style={{ flex: 1, minWidth: 180, maxWidth: 260 }}>
          <input
            data-testid="recently-deleted-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deleted items…"
            style={{
              width:        "100%",
              padding:      "5px 10px",
              fontSize:     T.fsSm5,
              border:       `1px solid ${T.border}`,
              borderRadius: 7,
              background:   T.card,
              color:        T.dark,
              boxSizing:    "border-box",
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          border:       `1px solid ${T.border}`,
          borderRadius: 10,
          overflowX:    "auto",
          background:   T.card,
        }}
      >
        {loading ? (
          <div
            style={{
              padding:   "32px 18px",
              textAlign: "center",
              color:     T.mid,
              fontSize:  T.fsSm,
            }}
          >
            Loading…
          </div>
        ) : error ? (
          <div
            style={{
              padding:      "20px 18px",
              color:        T.red,
              fontSize:     T.fsSm,
              background:   T.redSoft,
              borderRadius: 10,
            }}
          >
            {error}
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="recently-deleted-empty"
            style={{
              padding:   "40px 18px",
              textAlign: "center",
              color:     T.mid,
              fontSize:  T.fsSm,
            }}
          >
            {typeFilter !== "all" || search
              ? "No deleted items match your filters."
              : "No recently deleted items. Items appear here when they are deleted and can be restored within 30 days."}
          </div>
        ) : (
          <table
            style={{ width: "100%", borderCollapse: "collapse" }}
            data-testid="recently-deleted-table"
          >
            <thead>
              <tr>
                {["_icon", "Item", "Type", "Deleted by", "Deleted", "Auto-removes", "_actions"].map((h) => (
                  <th
                    key={h}
                    style={{
                      fontSize:      T.fsXxs,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color:         T.mid,
                      fontWeight:    T.fwExtrabold,
                      textAlign:     "left",
                      padding:       "9px 18px",
                      background:    T.tableHeadBg,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <ItemRow
                  key={`${item.type}-${item.id}`}
                  item={item}
                  onRestore={handleRestore}
                  restoring={restoring}
                />
              ))}
            </tbody>
          </table>
        )}

        {/* Footer */}
        <div
          style={{
            padding:        "10px 18px",
            borderTop:      items.length > 0 ? `1px solid ${T.border}` : "none",
            fontSize:       T.fsXs5,
            color:          T.mid,
            display:        "flex",
            justifyContent: "space-between",
            alignItems:     "center",
          }}
        >
          <span>Admin / editor only · viewers cannot see or restore deleted items</span>
          <span>Every delete and restore is recorded to audit_log</span>
        </div>
      </div>
    </div>
  );
}
